if (!customElements.get('iceyard-checkout-upsell')) {
  customElements.define('iceyard-checkout-upsell', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog?.showModal) return;
      this.abort = new AbortController();
      this.base = `${this.dataset.root.replace(/\/$/, '')}/`;
      this.strings = JSON.parse(this.querySelector('[data-upsell-strings]').textContent);
      this.money = new Intl.NumberFormat(this.dataset.locale || 'nb', { style: 'currency', currency: this.dataset.currency || 'NOK' });
      this.bypass = new WeakSet();
      this.seen = new Set();
      try { this.seen = new Set(JSON.parse(sessionStorage.getItem('iceyard-checkout-upsell-seen') || '[]')); } catch {}
      const options = { signal: this.abort.signal };
      document.addEventListener('submit', event => this.onCheckout(event), { ...options, capture: true });
      window.addEventListener('pageshow', () => this.disabledQuantities?.forEach(input => input.disabled = false), options);
      this.querySelector('[data-upsell-close]').addEventListener('click', () => this.dismiss(), options);
      this.querySelector('[data-upsell-skip]').addEventListener('click', () => this.resume(), options);
      this.querySelector('[data-upsell-add]').addEventListener('click', () => this.add(), options);
      this.querySelector('[data-upsell-size]').addEventListener('change', () => this.updatePrice(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); if (!this.adding) this.dismiss(); }, options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog && !this.adding) { const rect = this.dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dismiss(); } }, options);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.readRequest?.abort();
      this.close();
    }
    fingerprint(cart) {
      return (cart.items || []).map(item => `${item.variant_id || item.id}:${item.quantity}:${item.selling_plan_allocation?.selling_plan?.id || ''}`).sort().join('|');
    }
    remember(cart) {
      this.seen.add(typeof cart === 'string' ? cart : this.fingerprint(cart));
      this.seen = new Set([...this.seen].slice(-16));
      try { sessionStorage.setItem('iceyard-checkout-upsell-seen', JSON.stringify([...this.seen])); } catch {}
    }
    eligibleCart(cart) {
      return cart.items?.length > 0 && !cart.items.some(item => item.gift_card || item.selling_plan_allocation);
    }
    onCheckout(event) {
      const form = event.target;
      if (this.bypass.has(form)) { this.bypass.delete(form); return; }
      if (event.defaultPrevented || event.submitter?.name !== 'checkout' || !form.matches('form.cart-drawer__footer, form.cart-page')) return;
      if (new URL(form.action, location.origin).pathname.replace(/\/$/, '') !== `${this.base}cart`.replace(/\/$/, '')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.intent) return;
      this.intent = { form, button: event.submitter, type: form.matches('.cart-page') ? 'page' : 'drawer' };
      this.intent.button.setAttribute('aria-busy', 'true');
      this.prepare();
    }
    async read(url, signal) {
      const response = await fetch(url, { signal, headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('read');
      return response.json();
    }
    async prepare() {
      const intent = this.intent;
      this.readRequest = new AbortController();
      const timer = setTimeout(() => { this.readRequest.abort(); if (this.intent === intent) this.resume(); }, 2400);
      try {
        const signal = this.readRequest.signal;
        const [cart, manifest] = await Promise.all([this.read(`${this.base}cart.js`, signal), this.manifest ? Promise.resolve(this.manifest) : this.read(this.dataset.manifest, signal)]);
        this.manifest = manifest;
        if (this.intent !== intent) return;
        if (!this.eligibleCart(cart) || this.seen.has(this.fingerprint(cart))) { this.resume(); return; }
        const offer = await this.findOffer(cart, manifest, signal);
        if (this.intent !== intent) return;
        if (!offer) { this.resume(); return; }
        this.cart = cart;
        this.offer = offer;
        this.renderOffer();
        this.remember(cart);
        intent.button.removeAttribute('aria-busy');
        this.drawer = intent.form.closest('cart-drawer');
        if (this.drawer?.open) this.drawer.focusTrap?.pause();
        this.dialog.showModal();
        document.documentElement.classList.add('iy-checkout-upsell-open');
        this.querySelector('[data-upsell-close]').focus({ preventScroll: true });
      } catch { if (this.intent === intent) this.resume(); }
      finally { clearTimeout(timer); }
    }
    async findOffer(cart, manifest, signal) {
      const profiles = manifest.products || {};
      const inCart = new Set(cart.items.map(item => item.handle));
      const tested = new Set();
      for (const item of cart.items) {
        const source = profiles[item.handle];
        if (!source || /valgfritt|mystery|gavekort|custom/i.test(item.product_title || item.title || '')) continue;
        const color = Object.keys(source.variantsByColor || {}).find(key => source.variantsByColor[key].some(id => String(id) === String(item.variant_id || item.id)));
        if (!color || this.hasComplement(cart, item.handle, color, profiles)) continue;
        for (const handle of source.recommendations?.[color] || []) {
          if (inCart.has(handle) || tested.has(`${handle}|${color}`)) continue;
          tested.add(`${handle}|${color}`);
          const profile = profiles[handle];
          if (!profile || (source.type === 'pendant' && (profile.type !== 'necklace' || profile.gender !== source.gender))) continue;
          const product = await this.read(`${this.base}products/${encodeURIComponent(handle)}.js`, signal);
          if (!product.available || product.requires_selling_plan || product.gift_card || /valgfritt|mystery|gavekort|custom/i.test(product.title) || handle.startsWith('option-set-')) continue;
          const allowed = new Set((profile.variantsByColor?.[color] || []).map(String));
          const variants = product.variants.filter(variant => variant.available && allowed.has(String(variant.id)) && (variant.quantity_rule?.min || 1) === 1 && (variant.quantity_rule?.increment || 1) === 1);
          if (variants.length) return { product, variants, sourceHandle: item.handle, color };
        }
      }
      return null;
    }
    hasComplement(cart, sourceHandle, color, profiles) {
      const source = profiles[sourceHandle];
      if (!source) return false;
      const targets = (source.recommendations?.[color] || []).map(handle => ({ handle, profile: profiles[handle] })).filter(target => target.profile && (source.type !== 'pendant' || (target.profile.type === 'necklace' && target.profile.gender === source.gender)));
      return cart.items.some(item => {
        if (item.handle === sourceHandle || item.quantity <= 0 || item.selling_plan_allocation || item.gift_card) return false;
        const existing = profiles[item.handle];
        if (!existing?.variantsByColor?.[color]?.some(id => String(id) === String(item.variant_id || item.id))) return false;
        if (source.type === 'pendant' && (existing.type !== 'necklace' || existing.gender !== source.gender)) return false;
        return targets.some(target => target.handle === item.handle || (
          target.profile.family && Number.isFinite(target.profile.width) && target.profile.width > 0 &&
          existing.type === target.profile.type && existing.family === target.profile.family && existing.width === target.profile.width
        ));
      });
    }
    renderOffer() {
      const { product, variants } = this.offer;
      const first = variants[0];
      const url = `${this.base}products/${encodeURIComponent(product.handle)}?variant=${first.id}`;
      const title = this.querySelector('[data-upsell-title]');
      title.textContent = product.title;
      title.href = url;
      this.querySelector('[data-upsell-image-link]').href = url;
      const image = this.querySelector('[data-upsell-image]');
      const src = first.featured_image?.src || product.featured_image || product.images?.[0];
      if (src) { const imageURL = new URL(src, location.origin); imageURL.searchParams.set('width', '360'); image.src = imageURL.href; }
      else image.removeAttribute('src');
      image.alt = product.title;
      const select = this.querySelector('[data-upsell-size]');
      select.replaceChildren(new Option(this.strings.choose_size, '', true, true));
      select.options[0].disabled = true;
      variants.forEach(variant => select.add(new Option(variant.title, variant.id)));
      const defaultOnly = variants.length === 1 && variants[0].title === 'Default Title';
      if (defaultOnly) select.value = first.id;
      this.querySelector('[data-upsell-label]').hidden = defaultOnly;
      this.querySelector('[data-upsell-benefit]').hidden = this.dataset.discountConfirmed !== 'true';
      this.postAttempted = false;
      this.setAdding(false);
      this.updatePrice();
    }
    updatePrice() {
      const selected = this.offer.variants.find(variant => String(variant.id) === this.querySelector('[data-upsell-size]').value);
      const prices = this.offer.variants.map(variant => variant.price);
      const prefix = !selected && new Set(prices).size > 1 ? `${this.strings.from} ` : '';
      this.querySelector('[data-upsell-price]').textContent = `${prefix}${this.money.format((selected?.price || Math.min(...prices)) / 100)}`;
      if (!this.postAttempted) this.querySelector('[data-upsell-message]').textContent = '';
      if (selected) this.querySelectorAll('[data-upsell-title], [data-upsell-image-link]').forEach(link => { const url = new URL(link.href); url.searchParams.set('variant', selected.id); link.href = url.href; });
    }
    setAdding(adding) {
      this.adding = adding;
      this.querySelectorAll('[data-upsell-close], [data-upsell-skip], [data-upsell-size]').forEach(control => control.disabled = adding);
      const add = this.querySelector('[data-upsell-add]');
      add.disabled = adding || !!this.postAttempted;
      add.textContent = adding ? this.strings.adding : this.strings.add_continue;
      if (adding) add.setAttribute('aria-busy', 'true'); else add.removeAttribute('aria-busy');
    }
    async add() {
      if (this.adding || this.postAttempted || !this.intent) return;
      const select = this.querySelector('[data-upsell-size]');
      const variant = this.offer.variants.find(item => String(item.id) === select.value);
      if (!variant) { select.reportValidity(); select.focus(); return; }
      this.setAdding(true);
      this.querySelector('[data-upsell-message]').textContent = '';
      const request = new AbortController();
      const timer = setTimeout(() => request.abort(), 8000);
      try {
        const cart = await this.read(`${this.base}cart.js`, request.signal);
        if (!this.eligibleCart(cart)) { this.resume(true); return; }
        if (cart.items.some(item => item.handle === this.offer.product.handle)) { this.cartMayHaveChanged = true; this.remember(cart); this.resume(true); return; }
        const allowedSource = this.manifest.products[this.offer.sourceHandle].variantsByColor[this.offer.color];
        const source = cart.items.find(item => item.handle === this.offer.sourceHandle && allowedSource.some(id => String(id) === String(item.variant_id || item.id)));
        if (!source) { this.querySelector('[data-upsell-message]').textContent = this.strings.unavailable; return; }
        if (this.hasComplement(cart, this.offer.sourceHandle, this.offer.color, this.manifest.products)) { this.cartMayHaveChanged = true; this.remember(cart); this.resume(true); return; }
        this.postAttempted = true;
        this.cartMayHaveChanged = true;
        const response = await fetch(`${this.base}cart/add.js`, { method: 'POST', signal: request.signal, headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }) });
        if (!response.ok) {
          // Stock errors can partially modify the cart; reconcile before enabling retry.
          if (response.status >= 400 && response.status < 500) {
            const current = await this.read(`${this.base}cart.js`, request.signal);
            if (Array.isArray(current.items) && this.fingerprint(current) === this.fingerprint(cart)) this.postAttempted = false;
          }
          throw new Error('add');
        }
        this.remember({ items: [...cart.items, { variant_id: variant.id, quantity: 1 }] });
        this.resume(true);
      } catch { this.querySelector('[data-upsell-message]').textContent = this.strings.cart_error; }
      finally { clearTimeout(timer); this.setAdding(false); }
    }
    close() {
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('iy-checkout-upsell-open');
      if (this.drawer?.open) this.drawer.focusTrap?.unpause();
      this.drawer = null;
    }
    dismiss() {
      if (this.adding) return;
      this.intent?.button.removeAttribute('aria-busy');
      this.intent = null;
      this.close();
    }
    resume(force = false) {
      if (this.adding && !force) return;
      const intent = this.intent;
      if (!intent) return;
      this.intent = null;
      this.readRequest?.abort();
      intent.button.removeAttribute('aria-busy');
      this.close();
      let form = intent.form;
      if (!form.isConnected) form = document.querySelector(intent.type === 'page' ? 'form.cart-page' : 'form.cart-drawer__footer');
      if (!form) { location.assign(`${this.base}checkout`); return; }
      const button = form.querySelector('[name="checkout"]');
      if (!form.checkValidity()) { form.reportValidity(); return; }
      const quantities = this.cartMayHaveChanged ? [...form.querySelectorAll('[name="updates[]"]')].filter(input => !input.disabled) : [];
      this.disabledQuantities = quantities;
      quantities.forEach(input => input.disabled = true);
      let submitted;
      const capture = event => { submitted = event; };
      form.addEventListener('submit', capture, { once: true });
      this.bypass.add(form);
      try { form.requestSubmit(button || undefined); }
      catch { this.bypass.delete(form); location.assign(`${this.base}checkout`); }
      setTimeout(() => {
        form.removeEventListener('submit', capture);
        if (!submitted || submitted.defaultPrevented) quantities.forEach(input => input.disabled = false);
      }, 0);
    }
  });
}
